import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { Toggle } from "@/components/Toggle/Toggle";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import {
	useGetSalesforceOwnersQuery,
	useGetSalesforceUserMappingsQuery,
} from "@/pages/Private/redux/salesforce/salesforce.api";
import {
	useCreateSalesforceDealMutation,
	useGetSalesforceDealConfigQuery,
	useGetSalesforceDealCreationTriggerQuery,
	useGetSalesforcePipelinesQuery,
} from "@/pages/Private/redux/salesforce/salesforceDeal.api";

import { SalesforcePipelinePicker } from "./SalesforcePipelinePicker";
import { resolveSelectableStageId, resolveWritablePipelineId } from "./salesforcePipeline.utils";

interface ProspectInfo {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	title?: string | null;
	organizationName?: string | null;
}

const DEFAULT_TEMPLATE = "{firstName} {lastName} - {company}";

const buildDealName = (template: string | null | undefined, prospect: ProspectInfo): string =>
	(template || DEFAULT_TEMPLATE)
		.replace("{firstName}", prospect.firstName || "")
		.replace("{lastName}", prospect.lastName || "")
		.replace("{company}", prospect.organizationName || "")
		.replace("{email}", prospect.email || "")
		.replace("{title}", prospect.title || "")
		.trim();

/**
 * `Opportunity.CloseDate` is a Salesforce **Date**, not a DateTime, so it is sent as the bare
 * `YYYY-MM-DD` the date input already produces. Sending an ISO timestamp the way the Dynamics
 * modal does would be rejected.
 */
const toCloseDate = (date: string): string | undefined => date || undefined;

interface CreateSalesforceDealModalProps {
	isOpen: boolean;
	onClose: () => void;
	prospectId: number;
	prospect?: ProspectInfo;
}

export const CreateSalesforceDealModal: FunctionComponent<CreateSalesforceDealModalProps> = ({
	isOpen,
	onClose,
	prospectId,
	prospect,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.salesforce.createDeal.${key}`);

	const { user } = useAppSelector(authSelector);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig } = useGetSalesforceDealConfigQuery();
	const { data: pipelinesData } = useGetSalesforcePipelinesQuery();
	const { data: ownersData } = useGetSalesforceOwnersQuery();
	const { data: userMappingsData } = useGetSalesforceUserMappingsQuery();
	const { data: dealCreationTrigger } = useGetSalesforceDealCreationTriggerQuery();
	const [createDeal, { isLoading: isCreating }] = useCreateSalesforceDealMutation();

	const [createAsLead, setCreateAsLead] = useState(false);
	const [dealName, setDealName] = useState("");
	const [pipeline, setPipeline] = useState<string | null>(null);
	const [dealStage, setDealStage] = useState<string | null>(null);
	const [dealOwnerId, setDealOwnerId] = useState<string | null>(null);
	const [closeDate, setCloseDate] = useState("");
	const [amount, setAmount] = useState<number | null>(null);

	const pipelines = useMemo(() => pipelinesData?.pipelines ?? [], [pipelinesData]);

	useEffect(() => {
		if (typeof dealCreationTrigger?.createAsLead === "boolean") {
			setCreateAsLead(dealCreationTrigger.createAsLead);
		}
	}, [dealCreationTrigger]);

	useEffect(() => {
		if (!dealConfig) {
			return;
		}

		setPipeline(dealConfig.defaultPipelineId ?? null);
		setDealStage(dealConfig.defaultStageId ?? null);
		setAmount(dealConfig.defaultAmount ?? null);

		if (dealConfig.defaultOwnerId) {
			setDealOwnerId(dealConfig.defaultOwnerId);
		}

		setDealName(
			prospect
				? buildDealName(dealConfig.dealNameTemplate, prospect)
				: (dealConfig.dealNameTemplate ?? "")
		);

		if (dealConfig.closeDateOffsetDays) {
			const date = new Date();

			date.setDate(date.getDate() + dealConfig.closeDateOffsetDays);
			setCloseDate(date.toISOString().split("T")[0]);
		}
	}, [dealConfig, prospect]);

	useEffect(() => {
		if (userMappingsData && user?.id) {
			const mapping = userMappingsData.find((item) => item.userId === user.id);

			if (mapping) {
				setDealOwnerId(mapping.salesforceOwnerId);
			}
		}
	}, [userMappingsData, user?.id]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(ownersData?.owners ?? []).map((owner) => ({
				id: owner.id,
				title: owner.name ? `${owner.name} (${owner.email})` : owner.email,
			})),
		[ownersData]
	);

	const handleSubmit = async () => {
		if (!prospectId) {
			return;
		}

		// The synthesised pipeline's id is never written, and a stage from another record type
		// would be rejected by the org — resolve both rather than sending what is on screen.
		const writablePipelineId = resolveWritablePipelineId(pipelines, pipeline);
		const stageId = resolveSelectableStageId(pipelines, pipeline, dealStage);

		try {
			const result = await createDeal({
				prospectId,
				createAsLead,
				dealName: dealName.trim() || undefined,
				pipeline: createAsLead ? undefined : writablePipelineId || undefined,
				dealStage: createAsLead ? undefined : stageId || undefined,
				dealOwnerId: dealOwnerId || undefined,
				closeDate: createAsLead ? undefined : toCloseDate(closeDate),
				amount: createAsLead ? undefined : (amount ?? undefined),
			}).unwrap();

			handleOpenSnackbar(
				result.crmDeal.url ? `${ts("success")} — ${result.crmDeal.url}` : ts("success")
			);
			onClose();
		} catch {
			handleOpenSnackbar(ts("error"));
		}
	};

	return (
		<Modal
			disableSave={!prospectId}
			handleClose={onClose}
			handleSave={handleSubmit}
			isLoading={isCreating}
			isOpened={isOpen}
			size="sm"
			submitButtonText={createAsLead ? ts("submitLead") : ts("submitDeal")}
			title={createAsLead ? ts("titleLead") : ts("titleDeal")}
		>
			{renderMessageSnackbar()}
			<div className="grid grid-cols-1 gap-4">
				<div className="flex flex-col items-start gap-3">
					<span className="text-sm font-semibold text-text-meta">{ts("createAsLead")}</span>
					<div className="flex flex-row items-center gap-2">
						<Toggle
							isChecked={createAsLead}
							name="createAsLead"
							onChange={() => setCreateAsLead((prev) => !prev)}
						/>
						<span className="text-xs text-text-meta">{ts("createAsLeadHelp")}</span>
					</div>
				</div>

				<InputField
					handleChange={(event) => setDealName(event.target.value)}
					label={createAsLead ? ts("leadTopic") : ts("dealName")}
					name="dealName"
					placeholder={createAsLead ? ts("leadTopic") : ts("dealName")}
					value={dealName}
				/>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					{!createAsLead && (
						<SalesforcePipelinePicker
							pipelineId={pipeline}
							pipelines={pipelines}
							stageId={dealStage}
							ts={ts}
							onChange={({ pipelineId, stageId }) => {
								setPipeline(pipelineId);
								setDealStage(stageId);
							}}
						/>
					)}

					<Dropdown
						data={ownerOptions}
						defaultValue={ownerOptions.find((option) => option.id === dealOwnerId)}
						floating={true}
						handleSelect={(value) => setDealOwnerId(value.id as string)}
						isOptional={true}
						label={ts("owner")}
					/>

					{/* A Salesforce Lead has neither a close date nor an amount — hide both for one. */}
					{!createAsLead && (
						<>
							<InputField
								handleChange={(event) => setCloseDate(event.target.value)}
								isOptional={true}
								label={ts("closeDate")}
								name="closeDate"
								type="date"
								value={closeDate}
							/>

							<InputField
								handleChange={(event) => {
									const parsed = parseFloat(event.target.value);

									setAmount(Number.isFinite(parsed) && parsed >= 0 ? parsed : null);
								}}
								isOptional={true}
								label={ts("amount")}
								min={0}
								name="amount"
								type="number"
								value={amount ?? ""}
							/>
						</>
					)}
				</div>
			</div>
		</Modal>
	);
};
